import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { TransactionApi } from "../../../../../../../../services/Api/Admin/adminTypes";

interface MatchParams {
	transactionId: string;
}

const AdminTransactionPreviewPage = () => {
	const { transactionId } = useParams<MatchParams>();
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const [transaction, setTransaction] = useState<TransactionApi | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const confirmation = useConfirmation();
	const history = useHistory();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchTransaction();
	}, []);

	const fetchTransaction = async () => {
		setLoading(true);
		try {
			const res = await adminApi.getTransaction(Number(transactionId));
			setTransaction(res);
			handleChangeTabTitle(
				`${res.name} | ${t("modules.transaction.header.title")} | ${t("lib:go_component.navigation.admin")}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const onRemoveTransaction = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await adminApi.removeTransaction(Number(transactionId));
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
			history.push("/admin/transactions");
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (loading || !transaction) return <LoadingContainer />;

	const buttons: ButtonProps[] = [
		{
			variant: "light",
			title: t("common.action.edit", { ns: "lib" }),
			path: `/admin/transactions/${transaction?.id}/edit`,
		},
		{
			dropdown: true,
			title: t("common.action.remove", { ns: "lib" }),
			action: onRemoveTransaction,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			path: `/admin/transactions/${transaction?.id}/edit`,
		},
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: onRemoveTransaction,
		},
	];

	return (
		<div className="content">
			<MobileActions actions={mobileActions} />
			<Header
				title={`${t("modules.transaction.header.title")} "${transaction?.name}"`}
				buttons={isMobile ? [] : buttons}
			/>
			<div className="row">
				<div className="col-lg-10">
					<Card>
						<Card.Body>
							<dl className="mb-0">
								<dt>{t("lib:common.word.name")}</dt>
								<dd>{transaction.name}</dd>
							</dl>
							<dl className="mb-0">
								<dt>{t("lib:common.word.location")}</dt>
								<dd>{transaction.organization?.name}</dd>
							</dl>
							<dl className="mb-0">
								<dt>{t("lib:common.word.description")}</dt>
								<dd>{transaction.description}</dd>
							</dl>
							<dl className="mb-0">
								<dt>{t("common.word.total_price")}</dt>
								<dd>{FormatMoney(transaction.total_price)}</dd>
							</dl>
							<dl className="mb-0">
								<dt>{t("modules.transaction.field.paid_at.title")}</dt>
								<dd>{FormatDate(transaction.paid_at)}</dd>
							</dl>
						</Card.Body>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default AdminTransactionPreviewPage;
